import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Wind, Thermometer, Settings, Volume2, Home, Hotel, Briefcase, ArrowRight, Check, Wrench, Building2, Award, Film, UtensilsCrossed, Store, Dumbbell, Frame, Phone, Mail, MapPin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FlowLogo from "../components/FlowLogo";
import MobileCallButton from "../components/MobileCallButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 25 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
};

const staggerContainer = {
  initial: {},
  whileInView: { transition: { staggerChildren: 0.1 } },
  viewport: { once: true }
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" }
};

// Airflow Animation Component
const AirflowCanvas = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    
    class FlowParticle {
      constructor() { this.reset(); }
      reset() {
        this.x = -50;
        this.y = Math.random() * canvas.height;
        this.speed = 0.25 + Math.random() * 0.4;
        this.size = 1 + Math.random() * 1.5;
        this.opacity = 0.08 + Math.random() * 0.2;
        this.curve = (Math.random() - 0.5) * 0.4;
      }
      update() { this.x += this.speed; this.y += Math.sin(this.x * 0.008) * this.curve; if (this.x > canvas.width + 50) this.reset(); }
      draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2); ctx.fillStyle = `rgba(0, 175, 255, ${this.opacity})`; ctx.fill(); }
    }
    
    class FlowLine {
      constructor(index) { this.index = index; this.reset(); }
      reset() {
        this.x = -200; this.y = 80 + this.index * 100 + Math.random() * 50;
        this.length = 80 + Math.random() * 120; this.speed = 0.35 + Math.random() * 0.35;
        this.opacity = 0.1 + Math.random() * 0.15; this.curve = (Math.random() - 0.5) * 0.25;
      }
      update() { this.x += this.speed; if (this.x > canvas.width + 200) this.reset(); }
      draw() {
        const gradient = ctx.createLinearGradient(this.x, this.y, this.x + this.length, this.y);
        gradient.addColorStop(0, 'transparent'); gradient.addColorStop(0.3, `rgba(0, 175, 255, ${this.opacity})`);
        gradient.addColorStop(0.7, `rgba(0, 175, 255, ${this.opacity})`); gradient.addColorStop(1, 'transparent');
        ctx.beginPath(); ctx.moveTo(this.x, this.y);
        const cp1x = this.x + this.length * 0.3, cp1y = this.y + Math.sin(this.x * 0.004) * 25 * this.curve;
        const cp2x = this.x + this.length * 0.7, cp2y = this.y + Math.sin(this.x * 0.004 + 1) * 25 * this.curve;
        const endX = this.x + this.length, endY = this.y + Math.sin(this.x * 0.004 + 2) * 15 * this.curve;
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, endX, endY);
        ctx.strokeStyle = gradient; ctx.lineWidth = 1.5; ctx.stroke();
      }
    }
    
    for (let i = 0; i < 25; i++) particles.push(new FlowParticle());
    const lines = []; for (let i = 0; i < 5; i++) lines.push(new FlowLine(i));
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      lines.forEach(line => { line.update(); line.draw(); });
      particles.forEach(particle => { particle.update(); particle.draw(); });
      animationId = requestAnimationFrame(animate);
    };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animationId); };
  }, []);
  
  return <canvas ref={canvasRef} className="hero-airflow-canvas" />;
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section" id="hero" data-testid="hero-section">
      <div className="hero-grid-overlay" />
      <AirflowCanvas />
      <div className="container-flow relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[85vh] pt-24 pb-12 md:pt-28 md:pb-16">
          <motion.div className="lg:col-span-7 text-center" initial={{ opacity: 0, y: 35 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}>
            <span className="caption mb-5 block hero-caption-text" data-testid="hero-caption">
              Installation HVAC Premium<span className="hidden sm:inline"> •</span><br className="sm:hidden" /><span className="sm:ml-1">Paris & Île-de-France</span>
            </span>
            <h1 className="heading-hero mb-6 md:mb-8" data-testid="hero-title">
              Climatisation et ventilation <span className="bg-gradient-to-r from-[#00AFFF] to-[#0099DD] bg-clip-text text-transparent">haut de gamme</span> à Paris
            </h1>
            <p className="text-base md:text-lg text-[#475569] max-w-xl mx-auto mb-8 leading-relaxed" data-testid="hero-subtitle">
              FLOW installe des systèmes de climatisation et ventilation pour studios, hôtels, cinémas privés, bureaux et résidences premium en Île-de-France.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a href="#contact" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="hero-cta-primary">
                Demander un devis gratuit <ArrowRight size={18} />
              </a>
              <a href="#realisations" className="btn-secondary inline-flex items-center justify-center gap-2" data-testid="hero-cta-secondary">Voir nos réalisations</a>
            </div>
            <p className="text-sm text-[#64748B] mt-4">Réponse sous 24h • Devis personnalisé</p>
          </motion.div>
          <motion.div className="lg:col-span-5 hidden lg:flex lg:flex-col lg:justify-center" initial={{ opacity: 0, x: 35 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}>
            <div className="relative hero-image-container">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img src="/images/projects/salle-technique-hvac.png" alt="Installation HVAC professionnelle - local technique climatisation" className="w-full h-auto object-cover" loading="eager" />
              </div>
              <div className="absolute -bottom-6 -left-6 hero-stats-card">
                <div className="stat-number text-2xl">20+</div>
                <div className="stat-label">Années d'expérience</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { number: "20", label: "Années d'expérience" },
    { number: "150k€", label: "Budget moyen projet" },
    { number: "24h", label: "Délai de réponse" }
  ];
  return (
    <section className="stats-section py-6 md:py-8" data-testid="stats-section">
      <div className="container-flow">
        <motion.div className="grid grid-cols-3" variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true }}>
          {stats.map((stat, index) => (
            <motion.div key={index} className="stat-item" variants={staggerItem}>
              <span className="stat-number">{stat.number}</span>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Expertise Section
const ExpertiseSection = () => {
  return (
    <section className="section bg-[#F5F7FA]" id="expertise" data-testid="expertise-section">
      <div className="container-flow">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" {...fadeInUp}>
          <div className="lg:col-span-5 text-center">
            <span className="caption mb-4 block">Notre expertise</span>
            <h2 className="heading-section mb-6">Spécialiste en installation de climatisation à Paris</h2>
            <p className="text-[#475569] mb-5 leading-relaxed">FLOW est une entreprise spécialisée dans l'installation et la maintenance de systèmes de climatisation et ventilation haut de gamme. Notre expertise technique nous permet de réaliser des installations sur mesure pour les environnements les plus exigeants.</p>
            <p className="text-[#475569] mb-8 leading-relaxed">Nous travaillons en étroite collaboration avec les architectes et maîtres d'ouvrage pour intégrer parfaitement nos systèmes dans chaque projet.</p>
            <a href="#contact" className="btn-primary inline-flex items-center gap-2" data-testid="expertise-cta">Demander un devis gratuit <ArrowRight size={18} /></a>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { icon: Wrench, title: "Installation spécialisée", desc: "Pose professionnelle de systèmes HVAC adaptés à chaque environnement." },
                { icon: Wind, title: "Solutions sur mesure", desc: "Systèmes adaptés aux contraintes spécifiques de chaque projet." },
                { icon: Volume2, title: "Performance acoustique", desc: "Installation silencieuse pour studios et espaces sensibles au bruit." },
                { icon: Thermometer, title: "Confort optimal", desc: "Régulation thermique précise et qualité d'air intérieur garanties." }
              ].map((item, index) => (
                <motion.div key={index} className="service-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                  <div className="expertise-icon mb-4"><item.icon size={24} /></div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{item.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Installation Expertise Section (renamed from Technical Studies)
const InstallationExpertiseSection = () => {
  const features = [
    { icon: Wrench, title: "Installation spécialisée", description: "Installation de systèmes de climatisation et ventilation pour environnements exigeants : studios, hôtels, cinémas privés." },
    { icon: Volume2, title: "Environnements sensibles", description: "Solutions adaptées aux studios d'enregistrement, hôtels, bureaux premium et cinémas privés nécessitant un fonctionnement silencieux." },
    { icon: Building2, title: "Intégration architecturale", description: "Installation discrète et performante dans des projets architecturaux haut de gamme." }
  ];
  return (
    <section className="section bg-white technical-section" id="installation" data-testid="installation-section">
      <div className="technical-bg-pattern" />
      <div className="technical-airflow-lines" />
      <div className="container-flow relative z-10">
        <motion.div {...fadeInUp} className="mb-12 max-w-3xl mx-auto text-center">
          <span className="caption mb-4 block">Savoir-faire</span>
          <h2 className="heading-section mb-6">Notre expertise HVAC</h2>
          <p className="text-[#475569] leading-relaxed text-base md:text-lg">FLOW accompagne architectes, studios d'enregistrement et professionnels de l'hôtellerie dans l'installation de systèmes de climatisation et ventilation sur mesure. Nos installations prennent en compte les contraintes acoustiques et architecturales de chaque projet.</p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div key={index} className="technical-card" initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.12 }} data-testid={`installation-card-${index}`}>
              <div className="expertise-icon mb-5"><feature.icon size={24} /></div>
              <h3 className="text-lg font-semibold mb-3 text-[#0E1A2B]">{feature.title}</h3>
              <p className="text-[#475569] text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    { number: "01", icon: Wrench,   title: "Installation climatisation et ventilation", description: "Pose de systèmes haut de gamme : gainables, splits, VRV/VRF, CTA et VMC double flux.", route: "/installation" },
    { number: "02", icon: Settings, title: "Maintenance et dépannage HVAC",              description: "Contrats de maintenance préventive et interventions d'urgence toute l'année.",        route: "/maintenance" },
    { number: "03", icon: Wind,     title: "Solutions sur mesure",                        description: "Systèmes adaptés aux contraintes spécifiques de chaque environnement exigeant.",     route: "/expertise" }
  ];
  return (
    <section className="section bg-[#F5F7FA]" id="services" data-testid="services-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="mb-12 max-w-2xl mx-auto text-center">
          <span className="caption mb-4 block">Nos services</span>
          <h2 className="heading-section">Nos services en climatisation et ventilation</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.article key={index} className="service-card" initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.12 }} data-testid={`service-card-${index}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="expertise-icon"><service.icon size={22} /></div>
                <span className="service-number">{service.number}</span>
              </div>
              <h3 className="text-lg font-semibold mb-3 text-[#0E1A2B]">{service.title}</h3>
              <p className="text-[#475569] text-sm leading-relaxed mb-5">{service.description}</p>
              <Link to={service.route} className="text-[#00AFFF] text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`service-link-${service.number}`}>En savoir plus <ChevronRight size={16} /></Link>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Sectors Section - 3x3 grid with 9 sectors
const SectorsSection = () => {
  const sectors = [
    { icon: Volume2,         title: "Climatisation studio musique Paris",  subtitle: "Studios d'enregistrement",      description: "Installation de ventilation acoustique NC15-NC20 pour enregistrement professionnel.",                        route: "/secteurs/studios" },
    { icon: Hotel,           title: "Climatisation hôtels et hospitality", subtitle: "Hôtels boutique & Luxury",      description: "Systèmes discrets et performants, régulation chambre par chambre.",                                          route: "/secteurs/hotellerie" },
    { icon: Film,            title: "Climatisation cinéma privé",          subtitle: "Cinémas privés & Home cinéma",  description: "Installation de systèmes silencieux pour salles de cinéma privées et espaces home cinéma haut de gamme.",    route: "/contact" },
    { icon: Briefcase,       title: "Climatisation bureaux haut de gamme", subtitle: "Espaces professionnels",        description: "Qualité d'air optimale, confort thermique zone par zone.",                                                   route: "/secteurs/tertiaire" },
    { icon: Home,            title: "Climatisation résidentiel premium",   subtitle: "Appartements & Maisons de luxe", description: "Systèmes gainables invisibles, régulation intelligente.",                                                    route: "/secteurs/residentiel" },
    { icon: UtensilsCrossed, title: "Ventilation restaurants et bars",     subtitle: "Restaurants & Bars",            description: "Ventilation cuisine professionnelle et extraction haute performance.",                                       route: "/contact" },
    { icon: Store,           title: "Climatisation retail premium",        subtitle: "Retail & Boutiques luxe",       description: "Climatisation discrète pour espaces commerciaux premium.",                                                   route: "/contact" },
    { icon: Dumbbell,        title: "Climatisation salles de sport",       subtitle: "Salles de sport & Bien-être",   description: "Gestion thermique et ventilation pour environnements intensifs.",                                            route: "/contact" },
    { icon: Frame,           title: "Climatisation showrooms et galeries", subtitle: "Showrooms & Galeries",          description: "Climatisation précise pour espaces d'exposition.",                                                           route: "/contact" }
  ];
  return (
    <section className="section bg-white" id="secteurs" data-testid="sectors-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="mb-12 max-w-2xl mx-auto text-center">
          <span className="caption mb-4 block">Secteurs d'intervention</span>
          <h2 className="heading-section">Installation HVAC pour environnements exigeants</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sectors.map((sector, index) => (
            <Link to={sector.route} key={index} data-testid={`sector-card-${index}`} className="block">
              <motion.article className="sector-card h-full hover:shadow-lg transition-shadow cursor-pointer" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }}>
                <div className="flex items-start gap-4">
                  <div className="expertise-icon"><sector.icon size={24} /></div>
                  <div>
                    <span className="caption block mb-1">{sector.subtitle}</span>
                    <h3 className="text-base md:text-lg font-semibold mb-2 text-[#0E1A2B]">{sector.title}</h3>
                    <p className="text-[#475569] text-sm leading-relaxed">{sector.description}</p>
                  </div>
                </div>
              </motion.article>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const projects = [
    { title: "Restaurant Anima", location: "Paris", type: "Installation d'un système silencieux (grille acoustique) pour l'unité extérieure", image: "/images/projects/restaurant-anima.webp" },
    { title: "Studio Calogero", location: "Boulogne-Billancourt", type: "Climatisation et ventilation intégrées dans une isolation acoustique type boîte dans la boîte", image: "/images/projects/studio-calogero.webp" },
    { title: "Studio After Now", location: "Toulouse", type: "3 gainables avec système Airzone et mini VRV, installation complète CVC + CTA double flux", image: "/images/projects/studio-after-now.webp" }
  ];
  return (
    <section className="section bg-[#F5F7FA]" id="realisations" data-testid="projects-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="mb-12 max-w-2xl mx-auto text-center">
          <span className="caption mb-4 block">Portfolio</span>
          <h2 className="heading-section">Nos installations HVAC à Paris</h2>
        </motion.div>
        <div className="projects-grid">
          {projects.map((project, index) => (
            <Link to="/realisations" key={index} data-testid={`project-card-${index}`} className="block">
              <motion.article className="project-card h-full" initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.08 }}>
                <img src={project.image} alt={`${project.title} - ${project.type} - FLOW`} className="project-card-image" loading="lazy" />
                <div className="project-card-overlay" />
                <div className="project-card-content">
                  <span className="project-card-tag">{project.location}</span>
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-1">{project.title}</h3>
                  <p className="text-[#94A3B8] text-sm">{project.type}</p>
                </div>
              </motion.article>
            </Link>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/realisations" className="btn-secondary inline-flex items-center gap-2" data-testid="projects-see-all">
            Voir toutes nos réalisations <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Project References Section
const ProjectReferencesSection = () => {
  const references = [
    { title: "Restaurant Anima", location: "Paris", description: "Système silencieux avec grille acoustique pour unité extérieure" },
    { title: "Studio Calogero", location: "Boulogne-Billancourt", description: "Climatisation et ventilation en isolation acoustique boîte dans la boîte" },
    { title: "Studio After Now", location: "Toulouse", description: "3 gainables Airzone + mini VRV, CVC + CTA double flux" }
  ];
  return (
    <section className="section bg-white" id="references" data-testid="references-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="mb-12 text-center max-w-2xl mx-auto">
          <span className="caption mb-4 block">Confiance</span>
          <h2 className="heading-section mb-4">Références projets</h2>
          <p className="text-[#475569]">Des installations HVAC réalisées pour des clients exigeants à Paris et en Île-de-France.</p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {references.map((ref, index) => (
            <motion.div key={index} className="reference-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} data-testid={`reference-card-${index}`}>
              <div className="flex items-center gap-3 mb-3">
                <Award size={20} className="text-[#00AFFF]" />
                <span className="caption">{ref.location}</span>
              </div>
              <h3 className="text-base font-semibold text-[#0E1A2B] mb-2">{ref.title}</h3>
              <p className="text-[#475569] text-sm">{ref.description}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/realisations" className="btn-secondary inline-flex items-center gap-2" data-testid="references-see-all">
            Voir toutes nos réalisations <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Team Section - With real team members
const TeamSection = () => {
  const teamMembers = [
    { name: "Cyril Dumont", role: "Président & Ingénieur froid et climatisation", experience: "20 ans d'expérience" },
    { name: "Cyril Pires", role: "Chargé d'affaire", experience: "20 ans d'expérience" },
    { name: "Karim Dupont", role: "Chargé d'affaire & Technicien", experience: "5 ans d'expérience" }
  ];
  return (
    <section className="section bg-[#F5F7FA]" id="equipe" data-testid="team-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
          <span className="caption mb-4 block">Notre équipe</span>
          <h2 className="heading-section mb-6">Une équipe de spécialistes HVAC</h2>
          <p className="text-[#475569] leading-relaxed">FLOW réunit des techniciens certifiés et des chefs de projet expérimentés. Notre équipe cumule plus de 20 années d'expérience dans l'installation de systèmes de climatisation et ventilation haut de gamme.</p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {teamMembers.map((member, index) => (
            <motion.div 
              key={index} 
              className="team-member-card"
              initial={{ opacity: 0, y: 20 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }} 
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`team-member-${index}`}
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center mb-4 mx-auto">
                <span className="text-white font-bold text-xl">{member.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <h3 className="text-lg font-semibold text-[#0E1A2B] mb-1 text-center">{member.name}</h3>
              <p className="text-[#00AFFF] text-sm font-medium mb-2 text-center">{member.role}</p>
              <p className="text-[#475569] text-sm text-center">{member.experience}</p>
            </motion.div>
          ))}
        </div>

        <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
          {["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"].map((item, i) => (
            <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-lg shadow-sm">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center"><Check size={12} className="text-[#00AFFF]" /></div>
              <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
            </div>
          ))}
        </motion.div>

        <div className="text-center mt-10">
          <Link to="/equipe" className="btn-secondary inline-flex items-center gap-2" data-testid="team-see-all">
            Rencontrer l'équipe <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ nom: "", prenom: "", email: "", telephone: "", departement: "", objet: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nom || formData.nom.length < 2) newErrors.nom = "Nom requis";
    if (!formData.prenom || formData.prenom.length < 2) newErrors.prenom = "Prénom requis";
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Email invalide";
    if (!formData.telephone || formData.telephone.length < 10) newErrors.telephone = "Téléphone requis";
    if (!formData.departement) newErrors.departement = "Département requis";
    if (!formData.objet || formData.objet.length < 10) newErrors.objet = "Objet requis (min. 10 car.)";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error("Veuillez corriger les erreurs"); return; }
    setIsSubmitting(true); setSubmitStatus(null);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success"); toast.success("Demande envoyée !");
      setFormData({ nom: "", prenom: "", email: "", telephone: "", departement: "", objet: "" });
    } catch (error) {
      if (error.response?.status === 429) toast.error("Trop de demandes. Réessayez plus tard.");
      else toast.error("Erreur lors de l'envoi.");
      setSubmitStatus("error");
    } finally { setIsSubmitting(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  return (
    <section className="section bg-[#F5F7FA]" id="contact" data-testid="contact-section">
      <div className="container-flow">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <motion.div {...fadeInUp}>
            <span className="caption mb-4 block text-center">Contact</span>
            <h2 className="heading-section mb-6 text-center">Demander un devis gratuit</h2>
            <p className="text-[#475569] mb-4 leading-relaxed text-center">Décrivez votre projet et nos spécialistes vous contacteront pour discuter de vos besoins en climatisation et ventilation.</p>
            <p className="text-sm text-[#00AFFF] font-medium mb-8 text-center">Réponse sous 24h • Devis personnalisé</p>
            <div className="space-y-5 mb-8">
              <div className="flex items-center gap-4">
                <div className="expertise-icon w-12 h-12"><Phone size={20} /></div>
                <div>
                  <span className="caption block mb-0.5">Téléphone</span>
                  <a href="tel:+33650448840" className="text-[#00AFFF] font-medium hover:underline text-sm">06 50 44 88 40</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="expertise-icon w-12 h-12"><Mail size={20} /></div>
                <div>
                  <span className="caption block mb-0.5">Email</span>
                  <a href="mailto:contact@flow-air.com" className="text-[#00AFFF] font-medium hover:underline text-sm">contact@flow-air.com</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="expertise-icon w-12 h-12"><MapPin size={20} /></div>
                <div>
                  <span className="caption block mb-0.5">Adresse</span>
                  <span className="font-medium text-[#0E1A2B] text-sm">9 rue Quentin, 93300 Aubervilliers</span>
                </div>
              </div>
            </div>
            <a href="https://www.google.com/maps/search/9+rue+Quentin+93300+Aubervilliers+France" target="_blank" rel="noopener noreferrer" className="map-placeholder block" data-testid="map-link">
              <div className="text-center relative z-10">
                <MapPin size={32} className="text-[#00AFFF] mx-auto mb-2" />
                <span className="text-[#475569] text-sm font-medium">Voir sur Google Maps</span>
              </div>
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 25 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.15 }}>
            <AnimatePresence mode="wait">
              {submitStatus === "success" ? (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="success-message h-full flex flex-col items-center justify-center min-h-[480px]" data-testid="contact-success-message">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center mb-6 shadow-lg"><Check size={32} className="text-white" /></div>
                  <h3 className="text-xl font-semibold mb-3 text-[#0E1A2B]">Demande envoyée</h3>
                  <p className="text-[#475569] text-center max-w-sm mb-6 text-sm">Un spécialiste FLOW vous contactera sous 24 heures pour votre devis personnalisé.</p>
                  <button onClick={() => setSubmitStatus(null)} className="btn-secondary text-sm" data-testid="contact-new-request-button">Nouvelle demande</button>
                </motion.div>
              ) : (
                <motion.form onSubmit={handleSubmit} className="contact-form" initial={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="nom" className="caption block mb-1.5">Nom *</label>
                      <input type="text" id="nom" name="nom" value={formData.nom} onChange={handleChange} className={`input-field ${errors.nom ? 'border-red-400' : ''}`} placeholder="Votre nom" data-testid="contact-input-nom" />
                      {errors.nom && <span className="text-red-500 text-xs mt-1 block">{errors.nom}</span>}
                    </div>
                    <div>
                      <label htmlFor="prenom" className="caption block mb-1.5">Prénom *</label>
                      <input type="text" id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} className={`input-field ${errors.prenom ? 'border-red-400' : ''}`} placeholder="Votre prénom" data-testid="contact-input-prenom" />
                      {errors.prenom && <span className="text-red-500 text-xs mt-1 block">{errors.prenom}</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="email" className="caption block mb-1.5">Email *</label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`input-field ${errors.email ? 'border-red-400' : ''}`} placeholder="votre@email.com" data-testid="contact-input-email" />
                      {errors.email && <span className="text-red-500 text-xs mt-1 block">{errors.email}</span>}
                    </div>
                    <div>
                      <label htmlFor="telephone" className="caption block mb-1.5">Téléphone *</label>
                      <input type="tel" id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} className={`input-field ${errors.telephone ? 'border-red-400' : ''}`} placeholder="06 00 00 00 00" data-testid="contact-input-telephone" />
                      {errors.telephone && <span className="text-red-500 text-xs mt-1 block">{errors.telephone}</span>}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="departement" className="caption block mb-1.5">Département *</label>
                    <select id="departement" name="departement" value={formData.departement} onChange={handleChange} className={`input-field ${errors.departement ? 'border-red-400' : ''}`} data-testid="contact-select-departement">
                      <option value="">Sélectionnez un département</option>
                      <option value="01 - Ain">01 - Ain</option>
                      <option value="02 - Aisne">02 - Aisne</option>
                      <option value="03 - Allier">03 - Allier</option>
                      <option value="04 - Alpes-de-Haute-Provence">04 - Alpes-de-Haute-Provence</option>
                      <option value="05 - Hautes-Alpes">05 - Hautes-Alpes</option>
                      <option value="06 - Alpes-Maritimes">06 - Alpes-Maritimes</option>
                      <option value="07 - Ardèche">07 - Ardèche</option>
                      <option value="08 - Ardennes">08 - Ardennes</option>
                      <option value="09 - Ariège">09 - Ariège</option>
                      <option value="10 - Aube">10 - Aube</option>
                      <option value="11 - Aude">11 - Aude</option>
                      <option value="12 - Aveyron">12 - Aveyron</option>
                      <option value="13 - Bouches-du-Rhône">13 - Bouches-du-Rhône</option>
                      <option value="14 - Calvados">14 - Calvados</option>
                      <option value="15 - Cantal">15 - Cantal</option>
                      <option value="16 - Charente">16 - Charente</option>
                      <option value="17 - Charente-Maritime">17 - Charente-Maritime</option>
                      <option value="18 - Cher">18 - Cher</option>
                      <option value="19 - Corrèze">19 - Corrèze</option>
                      <option value="2A - Corse-du-Sud">2A - Corse-du-Sud</option>
                      <option value="2B - Haute-Corse">2B - Haute-Corse</option>
                      <option value="21 - Côte-d'Or">21 - Côte-d'Or</option>
                      <option value="22 - Côtes-d'Armor">22 - Côtes-d'Armor</option>
                      <option value="23 - Creuse">23 - Creuse</option>
                      <option value="24 - Dordogne">24 - Dordogne</option>
                      <option value="25 - Doubs">25 - Doubs</option>
                      <option value="26 - Drôme">26 - Drôme</option>
                      <option value="27 - Eure">27 - Eure</option>
                      <option value="28 - Eure-et-Loir">28 - Eure-et-Loir</option>
                      <option value="29 - Finistère">29 - Finistère</option>
                      <option value="30 - Gard">30 - Gard</option>
                      <option value="31 - Haute-Garonne">31 - Haute-Garonne</option>
                      <option value="32 - Gers">32 - Gers</option>
                      <option value="33 - Gironde">33 - Gironde</option>
                      <option value="34 - Hérault">34 - Hérault</option>
                      <option value="35 - Ille-et-Vilaine">35 - Ille-et-Vilaine</option>
                      <option value="36 - Indre">36 - Indre</option>
                      <option value="37 - Indre-et-Loire">37 - Indre-et-Loire</option>
                      <option value="38 - Isère">38 - Isère</option>
                      <option value="39 - Jura">39 - Jura</option>
                      <option value="40 - Landes">40 - Landes</option>
                      <option value="41 - Loir-et-Cher">41 - Loir-et-Cher</option>
                      <option value="42 - Loire">42 - Loire</option>
                      <option value="43 - Haute-Loire">43 - Haute-Loire</option>
                      <option value="44 - Loire-Atlantique">44 - Loire-Atlantique</option>
                      <option value="45 - Loiret">45 - Loiret</option>
                      <option value="46 - Lot">46 - Lot</option>
                      <option value="47 - Lot-et-Garonne">47 - Lot-et-Garonne</option>
                      <option value="48 - Lozère">48 - Lozère</option>
                      <option value="49 - Maine-et-Loire">49 - Maine-et-Loire</option>
                      <option value="50 - Manche">50 - Manche</option>
                      <option value="51 - Marne">51 - Marne</option>
                      <option value="52 - Haute-Marne">52 - Haute-Marne</option>
                      <option value="53 - Mayenne">53 - Mayenne</option>
                      <option value="54 - Meurthe-et-Moselle">54 - Meurthe-et-Moselle</option>
                      <option value="55 - Meuse">55 - Meuse</option>
                      <option value="56 - Morbihan">56 - Morbihan</option>
                      <option value="57 - Moselle">57 - Moselle</option>
                      <option value="58 - Nièvre">58 - Nièvre</option>
                      <option value="59 - Nord">59 - Nord</option>
                      <option value="60 - Oise">60 - Oise</option>
                      <option value="61 - Orne">61 - Orne</option>
                      <option value="62 - Pas-de-Calais">62 - Pas-de-Calais</option>
                      <option value="63 - Puy-de-Dôme">63 - Puy-de-Dôme</option>
                      <option value="64 - Pyrénées-Atlantiques">64 - Pyrénées-Atlantiques</option>
                      <option value="65 - Hautes-Pyrénées">65 - Hautes-Pyrénées</option>
                      <option value="66 - Pyrénées-Orientales">66 - Pyrénées-Orientales</option>
                      <option value="67 - Bas-Rhin">67 - Bas-Rhin</option>
                      <option value="68 - Haut-Rhin">68 - Haut-Rhin</option>
                      <option value="69 - Rhône">69 - Rhône</option>
                      <option value="70 - Haute-Saône">70 - Haute-Saône</option>
                      <option value="71 - Saône-et-Loire">71 - Saône-et-Loire</option>
                      <option value="72 - Sarthe">72 - Sarthe</option>
                      <option value="73 - Savoie">73 - Savoie</option>
                      <option value="74 - Haute-Savoie">74 - Haute-Savoie</option>
                      <option value="75 - Paris">75 - Paris</option>
                      <option value="76 - Seine-Maritime">76 - Seine-Maritime</option>
                      <option value="77 - Seine-et-Marne">77 - Seine-et-Marne</option>
                      <option value="78 - Yvelines">78 - Yvelines</option>
                      <option value="79 - Deux-Sèvres">79 - Deux-Sèvres</option>
                      <option value="80 - Somme">80 - Somme</option>
                      <option value="81 - Tarn">81 - Tarn</option>
                      <option value="82 - Tarn-et-Garonne">82 - Tarn-et-Garonne</option>
                      <option value="83 - Var">83 - Var</option>
                      <option value="84 - Vaucluse">84 - Vaucluse</option>
                      <option value="85 - Vendée">85 - Vendée</option>
                      <option value="86 - Vienne">86 - Vienne</option>
                      <option value="87 - Haute-Vienne">87 - Haute-Vienne</option>
                      <option value="88 - Vosges">88 - Vosges</option>
                      <option value="89 - Yonne">89 - Yonne</option>
                      <option value="90 - Territoire de Belfort">90 - Territoire de Belfort</option>
                      <option value="91 - Essonne">91 - Essonne</option>
                      <option value="92 - Hauts-de-Seine">92 - Hauts-de-Seine</option>
                      <option value="93 - Seine-Saint-Denis">93 - Seine-Saint-Denis</option>
                      <option value="94 - Val-de-Marne">94 - Val-de-Marne</option>
                      <option value="95 - Val-d'Oise">95 - Val-d'Oise</option>
                      <option value="971 - Guadeloupe">971 - Guadeloupe</option>
                      <option value="972 - Martinique">972 - Martinique</option>
                      <option value="973 - Guyane">973 - Guyane</option>
                      <option value="974 - La Réunion">974 - La Réunion</option>
                      <option value="976 - Mayotte">976 - Mayotte</option>
                    </select>
                    {errors.departement && <span className="text-red-500 text-xs mt-1 block">{errors.departement}</span>}
                  </div>
                  <div className="mb-6">
                    <label htmlFor="objet" className="caption block mb-1.5">Objet de la demande *</label>
                    <textarea id="objet" name="objet" value={formData.objet} onChange={handleChange} rows={4} className={`input-field resize-none ${errors.objet ? 'border-red-400' : ''}`} placeholder="Décrivez votre projet ou votre demande..." data-testid="contact-textarea-objet" />
                    {errors.objet && <span className="text-red-500 text-xs mt-1 block">{errors.objet}</span>}
                  </div>
                  <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="contact-submit-button">
                    {isSubmitting ? (<><div className="spinner" />Envoi...</>) : (<>Demander un devis gratuit <ArrowRight size={18} /></>)}
                  </button>
                  <p className="text-center text-[#94A3B8] text-xs mt-4">Réponse sous 24h • Devis personnalisé</p>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Partners Section - Brand logos
const PartnersSection = () => {
  const partners = ["DAIKIN", "MITSUBISHI", "HITACHI", "BOSCH"];
  return (
    <section className="py-12 md:py-16 bg-white border-t border-[#E2E8F0]" data-testid="partners-section">
      <div className="container-flow">
        <motion.div {...fadeInUp} className="text-center mb-10">
          <span className="caption mb-2 block">Marques installées</span>
          <p className="text-[#475569] text-sm">Nous travaillons avec les leaders du marché HVAC</p>
        </motion.div>
        <motion.div 
          className="flex flex-wrap justify-center items-center"
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.6 }}
        >
          {partners.map((partner, index) => (
            <span key={index} className="partner-logo-item" data-testid={`partner-${partner.toLowerCase().replace(' ', '-')}`}>
              {partner}
              {index < partners.length - 1 && <span className="partner-separator">·</span>}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Main HomePage Component
export default function HomePage() {
  return (
    <main data-testid="homepage">
      <Navigation />
      <HeroSection />
      <StatsSection />
      <ExpertiseSection />
      <InstallationExpertiseSection />
      <ServicesSection />
      <SectorsSection />
      <ProjectsSection />
      <ProjectReferencesSection />
      <TeamSection />
      <ContactSection />
      <PartnersSection />
      <Footer />
      <MobileCallButton />
      <div className="noise-overlay" aria-hidden="true" />
    </main>
  );
}
